/**
 * Helpers de apresentacao e parsing para a feature "devices".
 *
 * Toda logica que olha dentro de metaJson ou normaliza campos
 * heterogeneos vive aqui. Componentes consomem apenas estruturas
 * ja prontas e nao fazem JSON.parse no caminho de render.
 */
import type {
  CurrentMeta,
  Device,
  LatestPort,
  LatestSync,
  MetaFunctionEntry,
  ParsedMeta,
  TelemetryHealthPoint,
} from "./types";
import type { SignalType } from "../machines/types";

/* ---------------- IDs e labels ---------------- */

/** ID estavel a usar em rotas e endpoints (sempre device.id). */
export function getDeviceRouteId(d: Device): string {
  return String(d.id ?? "");
}

/** Rotulo curto pro device; cai pra mqttId/id quando nao ha label conhecido. */
export function getDeviceDisplayName(d: Device, parsed?: ParsedMeta | null): string {
  if (d.nomeFantasia?.trim()) return d.nomeFantasia.trim();
  if (parsed && typeof parsed === "object") {
    const p = parsed as Record<string, unknown>;
    if (typeof p.label === "string" && p.label.trim()) return p.label;
    if (typeof p.name === "string" && p.name.trim()) return p.name;
    if (typeof p.deviceName === "string" && p.deviceName.trim()) return p.deviceName;
    if (p.identity && typeof p.identity === "object" && !Array.isArray(p.identity)) {
      const identity = p.identity as Record<string, unknown>;
      if (typeof identity.friendlyName === "string" && identity.friendlyName.trim()) {
        return identity.friendlyName;
      }
    }
  }
  if (d.serialNumber && String(d.serialNumber).trim()) return String(d.serialNumber);
  return String(d.mqttId ?? d.id ?? "device");
}

/* ---------------- Datas ---------------- */

export function formatDateTime(iso?: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatRelative(iso?: string | null, now: Date = new Date()): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = now.getTime() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (Math.abs(sec) < 60) return `há ${sec}s`;
  const min = Math.round(sec / 60);
  if (Math.abs(min) < 60) return `há ${min}min`;
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return `há ${hr}h`;
  const day = Math.round(hr / 24);
  return `há ${day}d`;
}

export const DEFAULT_FRESHNESS_MS = 30_000;

export type Freshness = "fresh" | "stale" | "missing";

export function getAgeMs(iso?: string | null, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, now.getTime() - d.getTime());
}

export function getFreshness(
  iso?: string | null,
  maxAgeMs: number = DEFAULT_FRESHNESS_MS,
  now: Date = new Date(),
): Freshness {
  const ageMs = getAgeMs(iso, now);
  if (ageMs === null) return "missing";
  return ageMs <= maxAgeMs ? "fresh" : "stale";
}

export function isFresh(
  iso?: string | null,
  maxAgeMs: number = DEFAULT_FRESHNESS_MS,
  now: Date = new Date(),
): boolean {
  return getFreshness(iso, maxAgeMs, now) === "fresh";
}

/** Formata uptime em algo legivel: "3d 04:21:07" / "04:21:07" / "21min 7s". */
export function formatUptime(sec?: number | null): string {
  if (sec == null || Number.isNaN(sec) || sec < 0) return "-";
  const s = Math.floor(sec);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  if (days > 0) return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  if (hours > 0) return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  if (minutes > 0) return `${minutes}min ${seconds}s`;
  return `${seconds}s`;
}

/* ---------------- Status ---------------- */

export function classifyStatus(status?: string | null): "online" | "offline" | "neutral" {
  if (!status) return "neutral";
  const s = String(status).toLowerCase();
  if (
    s.includes("offline") ||
    s.includes("disconnected") ||
    s.includes("inactive") ||
    s.includes("error")
  ) {
    return "offline";
  }
  if (
    s.includes("online") ||
    s.includes("connected") ||
    s.includes("active") ||
    s === "ok"
  ) {
    return "online";
  }
  return "neutral";
}

export function isDeviceOnlineNow(
  device: Pick<Device, "status" | "lastSeenAt">,
  maxAgeMs: number = DEFAULT_FRESHNESS_MS,
  now: Date = new Date(),
): boolean {
  return classifyStatus(device.status) === "online" && isFresh(device.lastSeenAt, maxAgeMs, now);
}

/* ---------------- metaJson parser ---------------- */

export interface MetaParseResult {
  ok: boolean;
  /** Objeto parseado, se ok. */
  data: ParsedMeta | null;
  /** Mensagem de erro do parse, se nao ok. */
  error: string | null;
  /** Texto cru, util pra debug ou para mostrar em <pre>. */
  raw: string;
}

/**
 * Parse seguro de currentMeta.metaJson (string JSON).
 * - Aceita objeto top-level. Arrays/primitivos sao tratados como erro.
 * - Nunca lanca: erros sao reportados em `error`.
 */
export function parseMetaJson(meta?: CurrentMeta | null): MetaParseResult {
  const raw = meta?.metaJson ?? "";
  if (typeof raw !== "string" || raw.trim() === "") {
    return { ok: false, data: null, error: "metaJson vazio", raw: typeof raw === "string" ? raw : "" };
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        ok: false,
        data: null,
        error: "metaJson nao e um objeto JSON no topo",
        raw,
      };
    }
    return { ok: true, data: parsed as ParsedMeta, error: null, raw };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Falha ao parsear metaJson";
    return { ok: false, data: null, error: msg, raw };
  }
}

/** Extrai functions[] do parse de forma segura. */
export function getFunctions(parsed: ParsedMeta | null | undefined): MetaFunctionEntry[] {
  if (!parsed) return [];
  const fns = parsed.functions;
  if (!Array.isArray(fns)) return [];
  return fns.filter((f): f is MetaFunctionEntry => !!f && typeof f === "object");
}

/* ---------------- Channels (functions x latest ports) ---------------- */

/**
 * Linha de canal: junta a definicao da funcao (configurada via metaJson)
 * com o snapshot mais recente da mesma porta (latestPorts), quando existe.
 * E a visao mais util para o operador.
 */
export interface ChannelRow {
  key: string;
  portIndex: number | null;
  fn: MetaFunctionEntry | null;
  snapshot: LatestPort | null;
}

export type WidgetKind =
  | "digital-state"
  | "counter"
  | "pulse-meter"
  | "encoder"
  | "reset"
  | "analog-input"
  | "unknown";

export interface WidgetDefinition {
  kind: WidgetKind;
  mode: string;
  title: string;
  primaryValue: string;
  secondaryValue: string | null;
  isBinary: boolean;
  isTechnical: boolean;
}

export interface WidgetChannelRow extends ChannelRow {
  widget: WidgetDefinition;
}

export function buildChannels(
  functions: MetaFunctionEntry[],
  latestPorts: LatestPort[]
): ChannelRow[] {
  const byIndex = new Map<number, LatestPort>();
  for (const lp of latestPorts) {
    if (typeof lp.portIndex === "number") byIndex.set(lp.portIndex, lp);
  }

  const rows: ChannelRow[] = [];
  const seenIdx = new Set<number>();

  // 1) Uma linha por funcao definida no metaJson.
  for (let i = 0; i < functions.length; i++) {
    const f = functions[i];
    const idx = typeof f.p === "number" ? f.p : null;
    if (idx !== null) seenIdx.add(idx);
    rows.push({
      key: `fn-${i}-${idx ?? "?"}`,
      portIndex: idx,
      fn: f,
      snapshot: idx !== null ? (byIndex.get(idx) ?? null) : null,
    });
  }

  // 2) Snapshots de portas que nao tem funcao correspondente vao no final.
  for (const lp of latestPorts) {
    if (typeof lp.portIndex !== "number") continue;
    if (seenIdx.has(lp.portIndex)) continue;
    rows.push({
      key: `orphan-${lp.portIndex}`,
      portIndex: lp.portIndex,
      fn: null,
      snapshot: lp,
    });
  }

  rows.sort((a, b) => {
    const ai = a.portIndex ?? Number.POSITIVE_INFINITY;
    const bi = b.portIndex ?? Number.POSITIVE_INFINITY;
    return ai - bi;
  });

  return rows;
}

export function normalizeMode(mode?: unknown): string {
  if (mode === null || mode === undefined || mode === "") return "UNKNOWN";
  return String(mode).trim().toUpperCase();
}

export function getWidgetKind(mode?: unknown): WidgetKind {
  const normalized = normalizeMode(mode);
  if (normalized === "DI" || normalized === "LATCH") return "digital-state";
  if (normalized === "CT") return "counter";
  if (normalized === "PM") return "pulse-meter";
  if (normalized === "HSC") return "encoder";
  if (normalized === "RST") return "reset";
  if (normalized === "AIN") return "analog-input";
  return "unknown";
}

export function getWidgetTitle(kind: WidgetKind): string {
  switch (kind) {
    case "digital-state":
      return "DigitalStateWidget";
    case "counter":
      return "CounterWidget";
    case "pulse-meter":
      return "PulseMeterWidget";
    case "encoder":
      return "EncoderWidget";
    case "reset":
      return "ResetWidget";
    case "analog-input":
      return "AnalogInputWidget";
    default:
      return "UnknownWidget";
  }
}

export function buildWidgetDefinition(
  fn: MetaFunctionEntry | null,
  snapshot: LatestPort | null,
): WidgetDefinition {
  const mode = normalizeMode(fn?.m ?? snapshot?.mode);
  const kind = getWidgetKind(mode);
  const state = snapshot?.s;
  const acc = snapshot?.acc;

  return {
    kind,
    mode,
    title: getWidgetTitle(kind),
    primaryValue:
      kind === "digital-state" || kind === "reset"
        ? formatScalar(state)
        : formatScalar(acc),
    secondaryValue:
      kind === "digital-state" || kind === "reset"
        ? acc !== undefined
          ? `acc ${formatScalar(acc)}`
          : null
        : state !== undefined
          ? `s ${formatScalar(state)}`
          : null,
    isBinary: kind === "digital-state" || kind === "reset",
    isTechnical: kind === "reset",
  };
}

export function buildWidgetChannels(
  functions: MetaFunctionEntry[],
  latestPorts: LatestPort[],
): WidgetChannelRow[] {
  return buildChannels(functions, latestPorts).map((row) => ({
    ...row,
    widget: buildWidgetDefinition(row.fn, row.snapshot),
  }));
}

/** Formata `values` de uma funcao em string curta legivel. */
export function getSignalMetadata(
  fn: MetaFunctionEntry,
  signal: SignalType,
): {
  label?: string;
  unit?: string;
  kind?: string;
  decimals?: number;
  min?: number;
  max?: number;
} {
  const values = fn.values;
  if (!values || Array.isArray(values)) return {};
  const metadata = values[signal] as
    | {
        label?: string;
        unit?: string;
        kind?: string;
        decimals?: number;
        min?: number;
        max?: number;
      }
    | undefined;
  return {
    label: metadata?.label?.trim() || undefined,
    unit: metadata?.unit?.trim() || undefined,
    kind: typeof metadata?.kind === "string" ? metadata.kind : undefined,
    decimals: finiteOrUndefined(metadata?.decimals),
    min: finiteOrUndefined(metadata?.min),
    max: finiteOrUndefined(metadata?.max),
  };
}

function finiteOrUndefined(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

/* ---------------- Sync helpers ---------------- */

/** True quando configHashHex do sync casa com o da currentMeta. */
export function isHashAligned(meta: CurrentMeta | null, sync: LatestSync | null): boolean | null {
  const a = meta?.configHashHex;
  const b = sync?.configHashHex;
  if (!a || !b) return null;
  return a === b;
}

export function shortHash(hash?: string | null): string {
  if (!hash) return "-";
  const h = String(hash);
  return h.length <= 12 ? h : `${h.slice(0, 8)}...${h.slice(-4)}`;
}

export type TelemetryGapTone = "ok" | "warn" | "danger";

export interface TelemetryHealthSegment {
  startAt: string;
  endAt: string;
  gapMs: number;
  tone: TelemetryGapTone;
}

export interface TelemetryHealthSummary {
  windowStart: string;
  windowEnd: string;
  points: TelemetryHealthPoint[];
  segments: TelemetryHealthSegment[];
  gapCount: number;
  maxGapMs: number;
}

export function buildTelemetryHealthSummary(
  points: TelemetryHealthPoint[] = [],
  now: Date = new Date(),
  windowMs: number = 24 * 60 * 60 * 1000,
): TelemetryHealthSummary {
  const windowStartMs = now.getTime() - windowMs;
  const windowEndMs = now.getTime();
  const ordered = points
    .filter((point) => {
      const ts = Date.parse(point.measuredAt);
      return Number.isFinite(ts) && ts >= windowStartMs && ts <= windowEndMs;
    })
    .sort((a, b) => a.measuredAt.localeCompare(b.measuredAt));

  const segments: TelemetryHealthSegment[] = [];
  for (let index = 1; index < ordered.length; index++) {
    const prev = ordered[index - 1];
    const current = ordered[index];
    const gapMs = Date.parse(current.measuredAt) - Date.parse(prev.measuredAt);
    if (!Number.isFinite(gapMs) || gapMs < 0) continue;
    segments.push({
      startAt: prev.measuredAt,
      endAt: current.measuredAt,
      gapMs,
      tone: classifyTelemetryGap(gapMs),
    });
  }

  return {
    windowStart: new Date(windowStartMs).toISOString(),
    windowEnd: new Date(windowEndMs).toISOString(),
    points: ordered,
    segments,
    gapCount: segments.filter((segment) => segment.tone === "danger").length,
    maxGapMs: segments.reduce((max, segment) => Math.max(max, segment.gapMs), 0),
  };
}

export function classifyTelemetryGap(gapMs: number): TelemetryGapTone {
  if (gapMs > 10 * 60 * 1000) return "danger";
  if (gapMs > 5 * 60 * 1000) return "warn";
  return "ok";
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "1" : "0";
  return String(value);
}
